import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Alert, Button, Modal, Space, Spin, Table, Typography, message } from 'antd';
import { API } from '@/services/api.service';
import { errorText, formatAmount } from '../format';

const { Text } = Typography;

/**
 * A cash-flow file already sent to the group, set against the ledger. Its
 * months are frozen only when the accountant says so.
 *
 * @param {Object} props - Component props
 * @param {string} props.companyId - Company
 * @param {File} props.file - The .xlsx
 * @param {number} props.year - Year the file is for
 * @param {Function} props.onClose - Close without taking anything over
 * @param {Function} props.onApplied - Called after the months are frozen
 * @returns {JSX.Element} The dialog
 */
export default function CashFlowImportModal({ companyId, file, year, onClose, onApplied }) {
  const { t } = useTranslation('clientReports');

  const previewMutation = useMutation({
    mutationFn: () => API.clientReports.importCashFlow(companyId, file, { year, apply: false }),
    onError: async (e) => {
      message.error(await errorText(e));
      onClose();
    },
  });
  const applyMutation = useMutation({
    mutationFn: () => API.clientReports.importCashFlow(companyId, file, { year, apply: true }),
    onSuccess: () => {
      message.success(t('cashFlow.importApplied'));
      onApplied();
    },
    onError: async (e) => message.error(await errorText(e)),
  });

  useEffect(() => {
    previewMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const result = previewMutation.data?.data;
  const differences = (result?.months || []).flatMap((mo) =>
    mo.differences.map((key) => ({
      key: `${mo.month}-${key}`,
      month: mo.month,
      line: key,
      inFile: mo.values[key],
      ledger: mo.computed[key],
    }))
  );

  return (
    <Modal
      open
      width={820}
      title={t('cashFlow.importTitle')}
      onCancel={onClose}
      footer={
        <Space>
          <Button onClick={onClose}>{t('fccs.cancel')}</Button>
          <Button
            type="primary"
            className="btn-primary"
            disabled={!result}
            loading={applyMutation.isPending}
            onClick={() => applyMutation.mutate()}
          >
            {t('cashFlow.importApply')}
          </Button>
        </Space>
      }
    >
      {!result && (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      )}
      {result && (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          <Text>
            {t('cashFlow.importSummary', {
              year: result.year,
              months: result.months
                .map((mo) => String(mo.month).padStart(2, '0'))
                .join(', '),
              same: result.months.filter((mo) => !mo.differences.length).length,
            })}
          </Text>
          {result.opening.file !== null && (
            <Text type="secondary">
              {t('cashFlow.importOpening', {
                file: formatAmount(result.opening.file),
                ledger: formatAmount(result.opening.ledger),
              })}
            </Text>
          )}
          {result.unsupported.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message={t('cashFlow.importUnsupported', {
                list: result.unsupported
                  .map((u) => `${String(u.month).padStart(2, '0')}: ${u.label} ${formatAmount(u.amount)}`)
                  .join('; '),
              })}
            />
          )}
          {differences.length > 0 && (
            <Table
              size="small"
              pagination={false}
              scroll={{ y: 320 }}
              rowKey="key"
              dataSource={differences}
              columns={[
                {
                  title: t('cashFlow.month'),
                  dataIndex: 'month',
                  key: 'month',
                  width: 80,
                  render: (m) => `${String(m).padStart(2, '0')}.${result.year}`,
                },
                {
                  title: t('cashFlow.line'),
                  dataIndex: 'line',
                  key: 'line',
                  render: (key) => t(`cashFlow.lineShort.${key}`),
                },
                {
                  title: t('fccs.inFile'),
                  dataIndex: 'inFile',
                  key: 'inFile',
                  align: 'right',
                  render: formatAmount,
                },
                {
                  title: t('cashFlow.fromLedger'),
                  dataIndex: 'ledger',
                  key: 'ledger',
                  align: 'right',
                  render: formatAmount,
                },
              ]}
            />
          )}
          <Alert type="info" showIcon message={t('cashFlow.importNote')} />
        </Space>
      )}
    </Modal>
  );
}
